// ─── The day summary ─────────────────────────────────────────────────────────
//
// A single tap on a month tile opens this: who is booked that day, at what
// time, and a way to add somebody. The point is to scan a whole month without
// leaving it — the full day view is a double-tap away and a button away, but
// most of the time the question is just "who have I got on Thursday?".
//
// ── One component, two presentations ────────────────────────────────────────
//
// Desktop gets a panel that slides in from the right, beside the calendar.
// Narrow screens get a bottom sheet, because a side panel on a phone is a
// side panel with no room in it.
//
// Both are the same markup with different geometry, chosen by a media query
// rather than by measuring the window in JavaScript. A JS breakpoint has to
// re-measure on resize and rotation, gets it wrong on first paint, and is one
// more thing to keep in step with the CSS everything else uses.
//
// ── Why it is not a <dialog> ────────────────────────────────────────────────
//
// A modal would be wrong. The whole value is seeing the day WHILE the month is
// still there — tap Tuesday, tap Wednesday, tap Thursday, and read them one
// after another. Making it modal would put a backdrop over the thing the
// person is reading from.
//
// It is still dismissible by Escape and by an explicit close, because
// something that appears over part of the screen and cannot be got rid of is
// worse than either.

import { useEffect, useRef } from "react";
import { DayPanel, type DayBooking } from "./DayPanel.js";

export type DaySheetProps = {
  /**
   * The day being summarised, or null for nothing selected.
   *
   * ── Null renders a PLACEHOLDER of identical size, and that matters ────────
   *
   * On a wide screen this panel sits beside the calendar and takes 320px out
   * of the row. Rendering nothing when no day is chosen meant the calendar was
   * 320px wider until the first click and narrower afterwards — so clicking a
   * day moved every tile, measured at 78px right and 40% narrower.
   *
   * The second click of a double-click therefore landed on a DIFFERENT DAY's
   * tile, and could open the wrong day. Found on 2026-09-14 by driving it in a
   * real browser; jsdom gives every element a zero-sized box, so no test here
   * could have seen it.
   *
   * Keeping the slot occupied costs an empty panel on first load and buys a
   * calendar whose tiles never move under the pointer.
   */
  date: Date | null;
  bookings: DayBooking[];
  /** Closed by Escape, the close button, or the caller. */
  onClose: () => void;
  /** "Open the full day" — the reliable route on a phone, where double-tap is
   *  a fragile gesture, and the only route for a keyboard. */
  onOpenDay?: (date: Date) => void;
  /** "+ Add booking", for this specific day. */
  onAddBooking?: (date: Date) => void;
  /** Shown instead of the list while the day's bookings are still loading, so
   *  an empty list is never mistaken for "nobody is booked". */
  loading?: boolean;
  /** A failure to load. Rendered rather than swallowed: "nothing booked" and
   *  "could not find out" are different facts. */
  error?: string | null;
};

const ID = "nl-day-sheet";

export function DaySheet({
  date, bookings, onClose, onOpenDay, onAddBooking, loading = false, error = null,
}: DaySheetProps) {
  const panel = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => { if (e.key === "Escape") onClose(); };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [onClose]);

  // Moves focus in when the day changes, so a keyboard or screen-reader user
  // is taken to the thing that just appeared rather than left on the calendar
  // wondering whether anything happened. Not on the placeholder — there is
  // nothing there to read.
  useEffect(() => { if (date) panel.current?.focus(); }, [date]);

  if (!date) {
    return (
      <>
        <style>{SHEET_CSS}</style>
        {/* Same class, so the same width and the same place in the row. An
            empty div of a different size would reintroduce the reflow this
            exists to prevent. */}
        <div className="nl-day-sheet nl-day-sheet--empty" aria-hidden="true" data-testid="day-sheet-empty">
          <div className="nl-day-sheet__body nl-day-sheet__muted">
            Pick a day to see who is in.
          </div>
        </div>
      </>
    );
  }

  const heading = date.toLocaleDateString("en-GB", {
    weekday: "long", day: "numeric", month: "long",
  });

  return (
    <>
      <style>{SHEET_CSS}</style>
      <div
        ref={panel}
        id={ID}
        className="nl-day-sheet"
        role="region"
        aria-label={`${heading} — day summary`}
        tabIndex={-1}
        data-testid="day-sheet"
      >
        <header className="nl-day-sheet__head">
          <div>
            <div className="nl-day-sheet__eyebrow">Day summary</div>
            <h2 className="nl-day-sheet__title">{heading}</h2>
          </div>
          <button
            type="button"
            onClick={onClose}
            aria-label="Close day summary"
            className="nl-day-sheet__close"
            data-testid="day-sheet-close"
          >✕</button>
        </header>

        <div className="nl-day-sheet__body">
          {error ? (
            <p role="alert" className="nl-day-sheet__error">{error}</p>
          ) : loading ? (
            <p role="status" className="nl-day-sheet__muted">Loading…</p>
          ) : (
            <DayPanel
              date={date}
              bookings={bookings}
              emptyMessage="Nobody booked in yet."
            />
          )}
        </div>

        <footer className="nl-day-sheet__foot">
          {onAddBooking && (
            <button
              type="button"
              onClick={() => onAddBooking(date)}
              className="nl-day-sheet__primary"
              data-testid="day-sheet-add"
            >+ Add booking</button>
          )}
          {onOpenDay && (
            <button
              type="button"
              onClick={() => onOpenDay(date)}
              className="nl-day-sheet__secondary"
              data-testid="day-sheet-open"
            >Open full day</button>
          )}
        </footer>
      </div>
    </>
  );
}

/**
 * Real CSS rather than inline styles, because this needs a media query and a
 * `:hover`, and neither exists in a React style object.
 *
 * Every colour is a token. A hard-coded hex here would be the one surface that
 * does not re-skin with the rest of the product.
 */
const SHEET_CSS = `
.nl-day-sheet {
  display: flex;
  flex-direction: column;
  background: var(--nl-surface);
  color: var(--nl-ink);
  border: 1px solid var(--nl-stroke);
  /* Absolute radius. A percentage or 9999px turns a stretched panel into a
     capsule, which is the one shape Drea has ruled out twice. */
  border-radius: var(--nl-radius-card);
  outline: none;
}
.nl-day-sheet:focus-visible { box-shadow: 0 0 0 2px var(--nl-state-open); }

/* The reserved slot. Quieter than a real panel, and exactly the same size —
   the whole point is that the row's geometry does not change. */
.nl-day-sheet--empty { border-style: dashed; opacity: 0.6; }
/* Hidden on phones, where the sheet is a fixed-position overlay and takes no
   room out of the layout, so reserving space would just waste a screenful. */
@media (max-width: 720px) { .nl-day-sheet--empty { display: none; } }

.nl-day-sheet__head {
  display: flex; align-items: flex-start; gap: 12px;
  padding: 14px 16px 10px;
  border-bottom: 1px solid var(--nl-stroke-soft);
}
.nl-day-sheet__eyebrow {
  font-size: 0.7rem; letter-spacing: 0.08em; text-transform: uppercase;
  color: var(--nl-ink-faint);
}
.nl-day-sheet__title { margin: 2px 0 0; font-size: 1.05rem; font-weight: 600; }
.nl-day-sheet__close {
  margin-left: auto;
  min-height: var(--nl-hit-min); min-width: var(--nl-hit-min);
  border: 1px solid var(--nl-stroke); background: var(--nl-surface-raised);
  color: var(--nl-ink); border-radius: var(--nl-radius-control);
  font: inherit; cursor: pointer;
}
.nl-day-sheet__body { padding: 12px 16px; overflow-y: auto; }
.nl-day-sheet__muted { margin: 0; color: var(--nl-ink-dim); }
.nl-day-sheet__error { margin: 0; color: var(--nl-status-declined); }

.nl-day-sheet__foot {
  display: flex; gap: 8px; flex-wrap: wrap;
  padding: 12px 16px 14px;
  border-top: 1px solid var(--nl-stroke-soft);
  margin-top: auto;
}
.nl-day-sheet__primary, .nl-day-sheet__secondary {
  min-height: var(--nl-hit-min); padding: 0 16px;
  border-radius: var(--nl-radius-control); font: inherit; cursor: pointer;
  border: 1px solid var(--nl-stroke);
}
.nl-day-sheet__primary {
  background: var(--nl-state-open); color: var(--nl-state-open-ink);
  font-weight: 600; flex: 1 1 auto;
}
.nl-day-sheet__secondary { background: var(--nl-surface-raised); color: var(--nl-ink); }

/* ── Phones: a bottom sheet ───────────────────────────────────────────────
   Pinned to the bottom edge and capped at 70% of the viewport, so the month
   it was opened from stays visible above it. 100dvh rather than 100vh,
   because mobile browsers hide and show their toolbars and vh measures the
   wrong one — a sheet sized in vh gets its own footer pushed off-screen. */
@media (max-width: 720px) {
  .nl-day-sheet {
    position: fixed; left: 0; right: 0; bottom: 0; z-index: 40;
    max-height: 70dvh;
    border-radius: var(--nl-radius-card) var(--nl-radius-card) 0 0;
    border-bottom: none;
    box-shadow: 0 -8px 24px rgba(0, 0, 0, 0.35);
    padding-bottom: env(safe-area-inset-bottom);
  }
}

/* ── Wider screens: a panel beside the calendar ─────────────────────────── */
@media (min-width: 721px) {
  .nl-day-sheet {
    position: sticky; top: 16px;
    width: 320px; flex: 0 0 320px;
    max-height: calc(100dvh - 32px);

    /* min-width:0 and a hard max are not belt-and-braces. Without them a flex
       item cannot shrink below its CONTENT's min-content width, whatever its
       flex-basis says — the filled panel ran to 400px against the empty slot's
       320, so choosing a day still moved every tile by the 78px difference.
       Adding the placeholder alone did not fix the reflow; this is the other
       half. Measured in a browser: reading the flex shorthand never shows it.

       (No backticks in this comment. It lives inside a template literal, and a
       stray one silently ends the string — which is exactly how this file
       broke the build a moment ago, for the second time.) */
    min-width: 0;
    max-width: 320px;
  }
  .nl-day-sheet__body, .nl-day-sheet__head, .nl-day-sheet__foot { min-width: 0; }
  /* Long client names and service names wrap rather than widening the panel. */
  .nl-day-sheet__body { overflow-wrap: anywhere; }
}

@media (prefers-reduced-motion: no-preference) {
  .nl-day-sheet { animation: nl-day-sheet-in 140ms ease-out; }
  @keyframes nl-day-sheet-in {
    from { opacity: 0; transform: translateY(8px); }
    to   { opacity: 1; transform: none; }
  }
}
`;
